import React, {useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Preference} from "../types";
import {useOrganizationService} from "../../../services";
import {addOrgPreference, removeOrgPreference} from "../../../redux/slices/organization";
import {useJwt} from "../../../helpers/common";
import {HiCheck, HiX} from "react-icons/hi";

interface OrgPreferencesModalProps {
  onClose: () => void;
  onShowClearConfirm: () => void;
}

const MAX_ORG_MEMORIES = 25;

const OrgPreferencesModal: React.FC<OrgPreferencesModalProps> = ({ onClose, onShowClearConfirm }) => {
  const dispatch = useDispatch();
  const jwtData: any = useJwt();

  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, prefId: null });
  const _preferencesInput = useRef<HTMLTextAreaElement | null>(null)

  const {
    saveOrgPreferences,
    deleteSingleOrgPreferences
  } = useOrganizationService();

  const {
    orgPreferences
  } = useSelector((state: any) => state.organization);
  const storedPreferences = Array.isArray(orgPreferences) ? orgPreferences.length : 0;

  const handleDelete = (prefId: number) => {
    setDeleteConfirmModal({show: true, prefId: prefId})
  }

  const handleConfirmedDelete = async () => {
    if(!jwtData.org_id || !deleteConfirmModal.prefId) return;

    dispatch(removeOrgPreference(deleteConfirmModal.prefId));
    setDeleteConfirmModal({ show: false, prefId: null });
    await deleteSingleOrgPreferences(jwtData?.org_id, deleteConfirmModal.prefId);
  }

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if(!jwtData.org_id || !_preferencesInput.current) return;

    const value = _preferencesInput.current.value.trim();
    if (!value) return;

    dispatch(addOrgPreference({
      pref_id: -1,
      preference_text: value
    }))

    _preferencesInput.current.value = "";
    _preferencesInput.current.style.height = '50px';
    await saveOrgPreferences(jwtData?.org_id, { preference: value });
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.target.style.height = 'inherit';
    const newHeight = Math.min(e.target.scrollHeight, 200);
    e.target.style.height = `${newHeight}px`;
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  }

  return (
    <div
      className="fixed top-0 right-0 left-0 overflow-y-auto overflow-x-hidden md:inset-0 md:h-full items-center justify-center flex bg-black/10 backdrop-blur-sm h-[100vh] z-[9999]"
      onClick={onClose}
    >
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col mx-4" onClick={(e) => e.stopPropagation()}>
        {/* Enhanced Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Organization Memory
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 ">
                Train your AI with organization-specific knowledge
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onShowClearConfirm}
              disabled={!orgPreferences?.length}
              className={`flex items-center p-2 text-red-600 dark:text-red-400  rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-xs font-medium ${!orgPreferences?.length ? "opacity-50 cursor-not-allowed" : ""}`}
              title="Clear CadencePro's memory"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 flex flex-col min-h-0 p-0">
          <div className="flex-grow">
            <div className="h-[30rem] relative">
              <div className="text-token-text-primary h-full relative">
                {/* Enhanced scrollable content */}
                <div className="overflow-y-auto h-[25rem] px-4 py-2 pb-4 space-y-2">
                  {orgPreferences && orgPreferences.length ? orgPreferences.map((preference: Preference) => {
                    return (
                      <div
                        key={preference.pref_id}
                        className="group bg-gray-50 dark:bg-slate-700/50 rounded-xl p-1.5 border border-gray-200 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-500 hover:shadow-sm transition-all duration-200"
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="px-2 whitespace-pre-wrap flex text-sm items-center text-gray-800 dark:text-gray-200 leading-relaxed">
                              {preference.preference_text}
                            </div>
                          </div>

                          {deleteConfirmModal.show && deleteConfirmModal.prefId === preference.pref_id ? (
                            <div className="flex flex-row items-center justify-end gap-2">
                              <button
                                aria-label="Forget"
                                className="flex-shrink-0 p-2 hover:bg-green-100 dark:hover:bg-green-900/30 rounded text-green-600 dark:text-green-400 transition-colors"
                                onClick={() => handleConfirmedDelete()}
                              >
                                <HiCheck className="w-4 h-4" />
                              </button>

                              <button
                                aria-label="Forget"
                                className="flex-shrink-0 p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-600 dark:text-red-400 transition-colors"
                                onClick={() => setDeleteConfirmModal(prev => ({...prev, show: false}))}
                              >
                                <HiX className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              aria-label="Forget"
                              className="opacity-0 group-hover:opacity-100 p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200 text-red-600 dark:text-red-400"
                              onClick={() => handleDelete(preference.pref_id)}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  }) : (
                    <div className="flex h-full items-center justify-center py-12">
                      <div className="text-center max-w-sm">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-full mb-4">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          No preferences stored yet
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          Add your first piece of organizational preference below.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Enhanced input section */}
                <div className="mt-auto border-t border-gray-200 dark:border-slate-700 absolute bottom-0 w-full bg-white dark:bg-slate-800 rounded-b-2xl z-10">
                  <div className="p-3">
                    <div className="relative">
                        <textarea
                          placeholder="Add new organizational knowledge..."
                          ref={_preferencesInput}
                          onChange={handleInputChange}
                          onKeyDown={handleKeyDown}
                          className="w-full px-4 py-3 pr-12 overflow-hidden bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-xl text-gray-900
                          dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none
                          transition-all duration-200 h-[50px] max-h-[120px]"
                        />
                      <button
                        onClick={handleSubmit}
                        className="absolute right-2 top-2 p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                    <div className="px-3  text-[11px] text-gray-500 dark:text-gray-400 flex flex-col gap-1 text-center">
                    <span>
                      Cadence keeps up to {MAX_ORG_MEMORIES} org memories. Stored: {storedPreferences}/{MAX_ORG_MEMORIES}. Remove older entries to add new knowledge.
                    </span>
                    
                  </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
};

export default OrgPreferencesModal;